import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { BrandMark } from "@/components/brand/BrandMark";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { NAV } from "./nav";

/**
 * The rail.
 *
 * Queue counts come from `GET /admin/reports/counts` — one small endpoint
 * that exists precisely so the sidebar does not have to load the whole
 * dashboard to render five integers. Refetched every 45 seconds: often
 * enough that a badge appearing is how you find out something arrived,
 * rarely enough to be free.
 *
 * A zero renders NOTHING rather than a `0` pill. An empty queue is the
 * normal state, and a rail carrying five grey zeroes trains the eye to stop
 * looking at exactly the place we want it to look.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const api = useApi();
  const counts = useQuery({
    queryKey: ["admin", "reports", "counts"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/reports/counts");
      if (error) throw error;
      return data;
    },
    refetchInterval: 45_000,
  });

  return (
    <nav className="relative isolate flex h-full flex-col gap-5 overflow-hidden px-3 pb-5 pt-4 text-on-brand">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
      >
        <div className="absolute -right-20 top-16 h-48 w-48 rounded-[45%_55%_62%_38%/54%_38%_62%_46%] bg-brand-green" />
        <div className="absolute -left-16 top-[15.5rem] h-36 w-44 rotate-12 rounded-[58%_42%_37%_63%/43%_57%_43%_57%] bg-white/55" />
        <div className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full border-[42px] border-brand-green/55" />
      </div>

      <div className="px-2 pb-1">
        <div className="flex items-center gap-2.5">
          <BrandMark className="h-11 w-11 shrink-0" />
          <div>
            <span className="block text-[14px] font-bold leading-tight tracking-[-0.02em]">
              Find Me Matcha
            </span>
            <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-[0.18em] text-on-brand/60">
              Operator console
            </span>
          </div>
        </div>
      </div>

      {NAV.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <p className="px-2 pb-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-on-brand/55">
            {group.label}
          </p>
          {group.items.map((item) => {
            const badge = item.badgeKey
              ? (counts.data?.[item.badgeKey] ?? 0)
              : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "group flex min-h-9 items-center justify-between gap-2 rounded-[12px] px-2.5 py-1.5",
                    "text-[12.5px] transition-all",
                    isActive
                      ? "bg-white text-ink font-semibold shadow-panel"
                      : "text-on-brand/75 hover:bg-white/50 hover:text-on-brand",
                  )
                }
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <NavGlyph label={item.label} />
                  <span className="truncate">{item.label}</span>
                </span>
                {badge > 0 ? (
                  <span className="font-mono min-w-5 rounded-chip bg-brand-green px-1.5 text-center text-[10px] font-semibold leading-5 text-on-brand">
                    {badge}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function NavGlyph({ label }: { label: string }) {
  const common = "fill-none stroke-current";
  const path =
    label === "Dashboard"
      ? "M3 3h6v6H3zM15 3h6v10h-6zM3 15h6v6H3zM15 17h6v4h-6z"
      : label === "Goals"
        ? "M12 21a9 9 0 1 0-9-9M12 17a5 5 0 1 0-5-5M12 13a1 1 0 1 0-1-1M13 11l7-7M16 4h4v4"
        : label === "Cities" || label === "Cafes"
          ? "M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0ZM12 7v6M9 10h6"
          : label === "People"
            ? "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
            : label === "Reviews" || label === "Feedback"
              ? "M21 15a4 4 0 0 1-4 4H8l-5 3v-7a4 4 0 0 1-1-2.65V7a4 4 0 0 1 4-4h11a4 4 0 0 1 4 4z"
              : label === "Audit log"
                ? "M9 3h6l2 3h3v15H4V6h3zM8 11h8M8 15h8"
                : label === "Ops"
                  ? "M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4"
                  : "M4 5h16v12H9l-5 4z";
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} className={common} />
    </svg>
  );
}
